import { Component, lazy, Suspense, useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import Storefront from "@/pages/Storefront";

const Practice = lazy(() => import("@/pages/Practice"));
const TestSeries = lazy(() => import("@/pages/TestSeries"));
const Admin = lazy(() => import("@/pages/Admin"));
const ShiftSyncStaff = lazy(() => import("@/pages/ShiftSyncStaff"));
const Survey = lazy(() => import("@/pages/Survey"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Crm = lazy(() => import("@/pages/Crm"));
const IqChallenge = lazy(() => import("@/pages/IqChallenge"));
const Blog = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.default })));
const BlogPost = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogPost })));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-ink text-white font-display">
          <p className="text-2xl font-bold">Kuch toot gaya hai</p>
          <button
            onClick={() => { this.setState({ err: null }); window.location.reload(); }}
            className="rounded-full bg-amber-500 text-ink px-6 py-2 font-semibold"
            data-testid="error-reload-btn"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollRoot({ children }) {
  const location = useLocation();
  useEffect(() => {
    // admin panel utility hai — native scroll hi best (Lenis dialogs ke inner scroll hijack karta hai)
    if (location.pathname.startsWith("/admin")) return;
    const lenis = new Lenis({ autoRaf: true, lerp: 0.1 });
    window.__lenis = lenis;
    return () => { lenis.destroy(); window.__lenis = null; };
  }, [location.pathname]);
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return children;
}

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink">
      <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-white/10 border-t-amber-500" />
    </div>
  );
}

function StorefrontWithProduct() {
  const { productId } = useParams();
  return <Storefront autoOpenId={productId} />;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollRoot>
          <AuthProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Storefront />} />
                <Route path="/practice" element={<Practice />} />
                <Route path="/test-series" element={<TestSeries />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/p/:productId" element={<StorefrontWithProduct />} />
                <Route path="/shiftsync" element={<ShiftSyncStaff />} />
                <Route path="/survey" element={<Survey />} />
                <Route path="/iq" element={<IqChallenge />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/crm" element={<Crm />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </ScrollRoot>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
